// ContactsListPage — список контактов с поиском + пагинацией + FAB.
// R33c / U-7: живой поиск — param `q` (backend R33a), debounce 300 мс.
// R63 / U-34-D: поиск с флажками полей (SearchFieldBar → `fields` CSV; «Компания» = company_id).
import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Fab,
  TablePagination,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { contactsApi, type Contact } from '@/api/contacts';
import { SearchFieldBar, type SearchFieldOption, type SearchFieldValue } from '@/components/SearchFieldBar';
import { formatDate } from '@/lib/format';
import { useTableSort } from '@/hooks/useTableSort';
import { t } from '@/i18n';

const DEFAULT_PAGE_SIZE = 20;

/** Поле сортировки контактов (U-23: whitelist backend createdAt|updatedAt; UI — «Создана»). */
type ContactSortField = 'createdAt';

/** Направление сортировки. */
type SortDirection = 'asc' | 'desc';

/** Состояние сортировки: default createdAt,desc (backend default created_at DESC). */
interface ContactSort {
  field: ContactSortField;
  direction: SortDirection;
}

const DEFAULT_SORT: ContactSort = { field: 'createdAt', direction: 'desc' };

/** Флажки полей поиска контактов (SSOT §3.3; «Компания» — company_id, подзапрос по названию). */
function buildContactFieldOptions(): SearchFieldOption[] {
  return [
    { key: 'first_name', label: t('search.field.first_name') },
    { key: 'last_name', label: t('search.field.last_name') },
    { key: 'email', label: t('search.field.email') },
    { key: 'phone', label: t('search.field.phone') },
    { key: 'company_id', label: t('search.field.company_id') },
  ];
}

/** Страница списка контактов. */
export function ContactsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  // R63: q + fields в одном состоянии (SearchFieldBar — единая панель).
  const [searchValue, setSearchValue] = useState<SearchFieldValue>({ q: '', fields: [] });
  // U-23: состояние сортировки (канон DealsListPage: новый field → asc, активный → toggle).
  const { sort, handleSortClick } = useTableSort<ContactSortField>(DEFAULT_SORT, setPage);

  const query = useQuery({
    queryKey: ['contacts', page, searchValue.q, searchValue.fields.join(','), sort],
    queryFn: () =>
      contactsApi.list({
        page,
        size: DEFAULT_PAGE_SIZE,
        ...(searchValue.q ? { q: searchValue.q } : {}),
        ...(searchValue.fields.length > 0 ? { fields: searchValue.fields.join(',') } : {}),
        sort: `${sort.field},${sort.direction}`,
      }),
  });

  return (
    <Box>
      <PageHeader title="Контакты" subtitle="База контактов CRM" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <SearchFieldBar
          fieldsOptions={buildContactFieldOptions()}
          value={searchValue}
          onChange={(next) => {
            setPage(0);
            setSearchValue(next);
          }}
          placeholder={t('search.placeholder')}
          searchAriaLabel="Поиск контактов"
        />
      </Stack>
      {query.isLoading ? (
        <LoadingSpinner label="Загрузка контактов…" />
      ) : query.isError ? (
        <EmptyState
          title="Не удалось загрузить контакты"
          description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
        />
      ) : !query.data || query.data.content.length === 0 ? (
        <EmptyState
          title="Нет контактов"
          description="Создайте первый контакт"
          actionLabel="Создать контакт"
          onAction={() => navigate('/crm/contacts/new')}
        />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell>Имя</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Телефон</TableCell>
                  <TableCell>Должность</TableCell>
                  {/* U-23: сортируемая «Создана» (createdAt). */}
                  <TableCell sortDirection={sort.field === 'createdAt' ? sort.direction : false}>
                    <TableSortLabel
                      active={sort.field === 'createdAt'}
                      direction={sort.field === 'createdAt' ? sort.direction : 'desc'}
                      onClick={() => handleSortClick('createdAt')}
                    >
                      {t('crm.contacts.list.createdAt')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.content.map((c: Contact) => (
                  <TableRow
                    key={c.id}
                    hover
                    onClick={() => navigate(`/crm/contacts/${c.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {c.firstName} {c.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>{c.email ?? '—'}</TableCell>
                    <TableCell>{c.phone ?? '—'}</TableCell>
                    <TableCell>{c.position ?? '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(c.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={query.data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={DEFAULT_PAGE_SIZE}
            rowsPerPageOptions={[DEFAULT_PAGE_SIZE]}
            labelRowsPerPage={t('common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of')} ${count}`}
          />
        </Paper>
      )}
      <Stack direction="row" justifyContent="flex-end">
        <Fab
          component={RouterLink}
          to="/crm/contacts/new"
          color="primary"
          aria-label="Создать контакт"
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
        >
          <AddIcon />
        </Fab>
      </Stack>
    </Box>
  );
}
